import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { can } from "@/lib/permissions";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CurrencyDollar, FloppyDisk, UsersThree, Storefront } from "@phosphor-icons/react";

const toMinor = (v) => (v === "" || v == null ? null : Math.round(parseFloat(v) * 100));
const toDollars = (m) => (m == null ? "" : (m / 100).toFixed(2));

const CUSTOMER_FIELDS = [
  { key: "customer_price_minor", label: "Concurrent Call Limit Price",
    unit: "Price per concurrent channel (USD)",
    desc: "Charged when a customer buys additional concurrent call channels." },
  { key: "customer_ai_model_price_minor", label: "AI Model per min price",
    unit: "Price per minute (USD)",
    desc: "Per-minute rate for AI model usage on customer calls." },
  { key: "customer_tts_price_minor", label: "TTS per min price",
    unit: "Price per minute (USD)",
    desc: "Per-minute rate for text-to-speech used by customers." },
  { key: "customer_stt_price_minor", label: "STT per min price",
    unit: "Price per minute (USD)",
    desc: "Per-minute rate for speech-to-text used by customers." },
];

const RESELLER_FIELDS = [
  { key: "reseller_price_minor", label: "Concurrent Call Limit Price",
    unit: "Price per concurrent channel (USD)",
    desc: "Charged when a reseller buys additional concurrent call channels." },
  { key: "reseller_ai_model_price_minor", label: "AI Model per min price",
    unit: "Price per minute (USD)",
    desc: "Per-minute rate for AI model usage on reseller accounts." },
  { key: "reseller_tts_price_minor", label: "TTS per min price",
    unit: "Price per minute (USD)",
    desc: "Per-minute rate for text-to-speech on reseller accounts." },
  { key: "reseller_stt_price_minor", label: "STT per min price",
    unit: "Price per minute (USD)",
    desc: "Per-minute rate for speech-to-text on reseller accounts." },
  { key: "reseller_white_label_price_minor", label: "White label price",
    unit: "Price per month (USD)",
    desc: "Monthly white-label fee assigned to reseller accounts on Standard pricing." },
];

const EMPTY = Object.fromEntries(
  [...CUSTOMER_FIELDS, ...RESELLER_FIELDS].map((f) => [f.key, ""]),
);

function PriceRow({ testid, title, desc, unit, value, onChange, onSave, busy, canEdit }) {
  return (
    <Card className="p-5 rounded-xl border-border" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-accent grid place-items-center shrink-0">
          <CurrencyDollar className="w-5 h-5 text-primary" weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm sm:text-base">{title}</h3>
          <p className="text-sm text-muted-foreground mt-0.5">{desc}</p>
          <div className="mt-3 flex items-end gap-3">
            <div className="space-y-1.5 flex-1 max-w-[220px]">
              <Label className="text-xs">{unit}</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input data-testid={`${testid}-input`} type="number" min="0" step="0.01"
                  className="pl-7" value={value} disabled={!canEdit}
                  onChange={(e) => onChange(e.target.value)} placeholder="0.00" />
              </div>
            </div>
            {canEdit && (
            <Button data-testid={`${testid}-save`} onClick={onSave} disabled={busy}
              className="rounded-lg">
              <FloppyDisk className="w-4 h-4 mr-1.5" weight="bold" /> Save
            </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function PricingArea({ testid, icon: Icon, title, subtitle, fields, values, setValues, onSave, busy, canEdit = true }) {
  return (
    <section className="space-y-4" data-testid={testid}>
      <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 grid place-items-center shrink-0">
          <Icon className="w-5 h-5 text-primary" weight="duotone" />
        </div>
        <div>
          <h2 className="font-semibold text-lg">{title}</h2>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      <div className="grid gap-3">
        {fields.map((f) => (
          <PriceRow
            key={f.key}
            testid={`${testid}-${f.key}`}
            title={f.label}
            desc={f.desc}
            unit={f.unit}
            value={values[f.key]}
            onChange={(v) => setValues((prev) => ({ ...prev, [f.key]: v }))}
            onSave={() => onSave(f.key)}
            busy={busy}
            canEdit={canEdit}
          />
        ))}
      </div>
    </section>
  );
}

export default function BillingPricing() {
  const { user } = useAuth();
  const canEdit = can(user, "billing-pricing", "edit");
  const [values, setValues] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/pricing");
      const next = { ...EMPTY };
      for (const key of Object.keys(EMPTY)) {
        next[key] = toDollars(data[key]);
      }
      setValues(next);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (key) => {
    const minor = toMinor(values[key]);
    if (minor == null || isNaN(minor) || minor < 0) {
      toast.error("Enter a valid non-negative price."); return;
    }
    setBusy(true);
    try {
      const { data } = await api.put("/billing/pricing", { [key]: minor });
      setValues((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(EMPTY)) next[k] = toDollars(data[k]);
        return next;
      });
      toast.success("Price saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="billing-pricing-page">
      <PageHeader title="Billing — Pricing"
        subtitle="Standard prices for customers and resellers. New signups and accounts on Standard pricing inherit these snapshots; changing a price never rewrites already-assigned accounts." />
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="grid gap-10 max-w-3xl">
          <PricingArea
            testid="pricing-customer-area"
            icon={UsersThree}
            title="Customer pricing"
            subtitle="Applied to customers who sign up from the public signup page, and to customers created with Standard pricing."
            fields={CUSTOMER_FIELDS}
            values={values}
            setValues={setValues}
            onSave={save}
            busy={busy}
            canEdit={canEdit}
          />
          <PricingArea
            testid="pricing-reseller-area"
            icon={Storefront}
            title="Reseller pricing"
            subtitle="Applied to reseller accounts created with Standard pricing (concurrent channels, white label, and AI / TTS / STT per minute)."
            fields={RESELLER_FIELDS}
            values={values}
            setValues={setValues}
            onSave={save}
            busy={busy}
            canEdit={canEdit}
          />
        </div>
      )}
    </div>
  );
}
